import { loadCashMovements } from "./cashDrawer";
import { loadOrders, tryLoadOrdersInRange } from "./orders";
import { isSupabaseConfigured, supabase } from "../lib/supabase";

const Z_REPORTS_KEY = "almoune_z_reports";

export const getZReports = () => {
  try {
    const raw = localStorage.getItem(Z_REPORTS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

export const getLastZReport = () => {
  const reports = getZReports();
  return reports.length > 0 ? reports[0] : null;
};

const filterOrdersByRange = (orders, fromDate, toDate) =>
  orders.filter((o) => {
    const date = new Date(o.date || o.created_at || o.createdAt);
    if (Number.isNaN(date.getTime())) return false;
    if (fromDate && date < fromDate) return false;
    if (toDate && date > toDate) return false;
    return true;
  });

export const filterOrdersByUnit = (orders = [], unit) => {
  if (!unit) return orders;
  return orders.filter((o) => (o.businessUnit || "alimentation") === unit);
};

export const generateXZReportData = async ({ from, to, unit } = {}) => {
  let allOrders;
  let sessionOrders;
  let allMovements;
  let sessionMovements;
  const lastZ = getLastZReport();
  const lastZDate = lastZ ? new Date(lastZ.created_at) : new Date(0); // If no Z report, start from beginning

  const fromDate = from ? new Date(from) : null;
  const toDate = to ? new Date(to) : null;

  if (fromDate) {
    // Date-range query: use filtered Supabase query, then refine locally
    try {
      allOrders = await tryLoadOrdersInRange(
        fromDate.toISOString(),
        toDate ? toDate.toISOString() : undefined
      );
    } catch (e) {
      allOrders = [];
    }
  } else {
    allOrders = await loadOrders();
  }

  allOrders = filterOrdersByUnit(allOrders, unit);

  allMovements = await loadCashMovements();

  if (fromDate) {
    const upperBound = toDate
      ? new Date(toDate.getTime() + 24 * 60 * 60 * 1000 - 1)
      : null;
    sessionOrders = filterOrdersByRange(allOrders, fromDate, upperBound);
    sessionMovements = filterOrdersByRange(allMovements, fromDate, upperBound);
  } else {
    sessionOrders = allOrders.filter(
      (o) => new Date(o.date || o.created_at || o.createdAt) > lastZDate
    );
    sessionMovements = allMovements.filter(
      (m) => new Date(m.created_at) > lastZDate
    );
  }
  
  // 3. Calculate Totals
  let totalSalesRevenue = 0;
  let totalSalesCount = 0;
  let totalReturnsRevenue = 0;
  let totalReturnsCount = 0;
  const orderModeBreakdown = {
    magasin: 0,
    emporter: 0,
    livrer: 0
  };
  const paymentBreakdown = {
    ESPECES: 0,
    CB: 0,
    CHEQUE: 0,
    VIREMENT: 0,
    TR: 0,
    AVOIR: 0,
    DIFFERE: 0,
    AUTRE: 0
  };
  
  sessionOrders.forEach(order => {
    // If total is negative, it is a return (Avoir)
    if (order.total < 0) {
      totalReturnsRevenue += Math.abs(order.total);
      totalReturnsCount++;
    } else {
      totalSalesRevenue += order.total;
      totalSalesCount++;
      
      const mode = order.orderMode || "in_store";
      if (mode === "in_store" || mode === "magasin") {
        orderModeBreakdown.magasin += order.total;
      } else if (mode === "takeaway" || mode === "emporter") {
        orderModeBreakdown.emporter += order.total;
      } else if (mode === "delivery" || mode === "livrer") {
        orderModeBreakdown.livrer += order.total;
      } else {
        orderModeBreakdown.magasin += order.total; // fallback
      }
    }
    
    // Sum payments
    if (order.payments && Array.isArray(order.payments)) {
      order.payments.forEach(p => {
        const method = (p.method || "AUTRE").toUpperCase();
        if (paymentBreakdown[method] !== undefined) {
          paymentBreakdown[method] += p.amount;
        } else {
          paymentBreakdown.AUTRE += p.amount;
        }
      });
    }
  });
  
  // 4. Calculate Cash Drawer Balance
  let cashMovementsIn = 0;
  let cashMovementsOut = 0;
  let openingFloat = 0;
  
  sessionMovements.forEach(m => {
    if (m.type === "in") {
      if (m.reason === "fond_caisse") openingFloat += m.amount;
      cashMovementsIn += m.amount;
    } else {
      cashMovementsOut += m.amount;
    }
  });
  
  const expectedCash = openingFloat + paymentBreakdown.ESPECES + (cashMovementsIn - openingFloat) - cashMovementsOut;
  
  // 5. Calculate Top Products (optional but nice)
  const productSales = {};
  sessionOrders.forEach(order => {
    if (order.total > 0 && order.items) {
      order.items.forEach(item => {
        if (!item.isReturn) {
          productSales[item.title] = (productSales[item.title] || 0) + item.quantity;
        }
      });
    }
  });
  const topProducts = Object.entries(productSales)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([name, qty]) => ({ name, qty }));

  return {
    lastZDate: lastZ ? lastZ.created_at : null,
    currentDate: new Date().toISOString(),
    totalSalesRevenue,
    totalSalesCount,
    totalReturnsRevenue,
    totalReturnsCount,
    orderModeBreakdown,
    paymentBreakdown,
    cashMovementsIn,
    cashMovementsOut,
    openingFloat,
    expectedCash,
    topProducts,
    sessionOrdersCount: sessionOrders.length
  };
};

export const archiveZReport = async (reportData, actualCash, cashierId, cashierName) => {
  const newZ = {
    id: `Z-${Date.now()}`,
    ...reportData,
    actualCash: parseFloat(actualCash) || 0,
    difference: (parseFloat(actualCash) || 0) - reportData.expectedCash,
    cashierId: cashierId || "unknown",
    cashierName: cashierName || "Caissier",
    created_at: new Date().toISOString(),
    synced: false
  };
  
  const zReports = getZReports();
  const updatedZ = [newZ, ...zReports];
  localStorage.setItem(Z_REPORTS_KEY, JSON.stringify(updatedZ));
  
  // Optional: Sync to Supabase
  if (isSupabaseConfigured && supabase) {
    try {
      const { error } = await supabase.from("z_reports").insert([{
        id: newZ.id,
        report_data: newZ,
        created_at: newZ.created_at,
        cashier_id: newZ.cashierId
      }]);
      if (!error) {
        newZ.synced = true;
        localStorage.setItem(Z_REPORTS_KEY, JSON.stringify(
          updatedZ.map(r => r.id === newZ.id ? newZ : r)
        ));
      }
    } catch(e) {
      console.error("Z sync error", e);
    }
  }
  
  return newZ;
};


